import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import PageHeader from '../../components/DashboardComponents/PageHeader';
import SearchInput from '../../components/Listing/SearchInput';
import FilterDropdown from '../../components/Listing/FilterDropdown';
import Pagination from '../../components/Listing/Pagination';
import CustomButton from '../../components/OnboardingComponents/CustomButton';
import EmptyState from '../../components/Common/EmptyState';
import TruncatedText from '../../components/Common/TruncatedText';
import { Avatar } from '../../components/DashboardComponents/AvatarGroup';
import { AddCollaboratorModal } from '../../components/Modals';
import { ICONS } from '../../constants/icons';
import { get } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { getApiErrorMessage } from '../../helpers/apiError';
import { getUserAvatarUrl } from '../../helpers/userAvatar';
import { isSameUserId, nameWithYou } from '../../helpers/userDisplay';

const PAGE_SIZE = 10;
const SKELETON_ROWS = 8;
const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_SORT_BY = 'created_at';
const DEFAULT_SORT_DIRECTION = 'desc';

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Joined On' },
  { value: 'status', label: 'Status' },
  { value: 'role', label: 'Role' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const STATUS_CLASS = {
  pending: 'pending',
  active: 'active',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
};

const formatStatusLabel = (status) => {
  if (!status) return '—';
  const normalized = String(status).trim().toLowerCase();
  if (!normalized) return '—';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const TABLE_COLUMNS = 7;

const formatDate = (value) => {
  if (!value) return '—';
  const date = dayjs(value);
  return date.isValid() ? date.format('MMM DD, YYYY') : '—';
};

const firstDefined = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== '');

const formatXp = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return `${amount.toLocaleString('en-US')}XP`;
};

const formatProjects = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount;
};

const mapCollaborator = (item) => {
  const user = item.user || item.invitee || {};
  const invite = item.invite || {};
  const hasUser = Boolean(user?.id || user?.email || user?.first_name || user?.full_name);
  const inviteEmail =
    invite.email ||
    invite.invitee_email ||
    invite.invited_email ||
    item.email ||
    item.invitee_email ||
    '';
  const roleType = item.role_types?.[0] || item.role_type || {};
  const name = hasUser
    ? user.full_name ||
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
      user.name ||
      item.full_name ||
      user.email ||
      'Unknown'
    : inviteEmail || 'External collaborator';

  const role = roleType.public_name || roleType.name || '—';
  const statusRaw = String(
    item.status || invite.status || ''
  )
    .trim()
    .toLowerCase();

  return {
    id: item.id || user.id || invite.id || inviteEmail,
    userId: user.id || item.user_id || null,
    name,
    role: role || '—',
    status: statusRaw,
    statusLabel: formatStatusLabel(statusRaw),
    statusClass: STATUS_CLASS[statusRaw] || 'pending',
    avatar: hasUser ? getUserAvatarUrl(user) : null,
    xp: formatXp(
      firstDefined(
        user.total_xp,
        user.xp,
        user.xp_points,
        user.xp_point?.points,
        item.xp,
        item.total_xp
      )
    ),
    projects: formatProjects(
      firstDefined(
        user.projects_count,
        user.total_projects,
        item.projects_count,
        item.projects_total
      )
    ),
    joinedOn: formatDate(item.joined_at || item.created_at),
    isExternal: !hasUser,
  };
};

const Collaborators = () => {
  const navigate = useNavigate();
  const currentUserId = useSelector((state) => state.auth?.currentUser?.id);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [collaborators, setCollaborators] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [search]);

  const fetchCollaborators = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const params = {
      involvement: 'all',
      page: currentPage,
      per_page: PAGE_SIZE,
      page_size: PAGE_SIZE,
      sort_by: sortBy,
      sort_direction: sortDirection,
      distinct: 1,
      'with[]': ['user', 'project', 'roleTypes', 'invite'],
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    if (statusFilter) {
      params.status = statusFilter;
    }

    try {
      const response = await get(endpoints.getProjectInvitations, { params });

      if (response?.data?.success) {
        const result = response.data.result || {};
        const list = Array.isArray(result) ? result : result.data || [];
        const rows = list.map(mapCollaborator);
        const total = result.total || rows.length;
        const perPage = result.per_page || result.page_size || PAGE_SIZE;

        setCollaborators(rows);
        setTotalItems(total);
        setPageSize(perPage);

        const totalPages = Math.max(1, Math.ceil(total / perPage));
        if (currentPage > totalPages) {
          setCurrentPage(totalPages);
        }
      } else {
        setError(
          getApiErrorMessage(response, 'Failed to load collaborators')
        );
        setCollaborators([]);
        setTotalItems(0);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load collaborators'));
      setCollaborators([]);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearch, sortBy, sortDirection, statusFilter]);

  useEffect(() => {
    fetchCollaborators();
  }, [fetchCollaborators]);

  const openProfile = (userId) => {
    if (!userId) return;
    navigate(`/public/profile/${userId}`);
  };

  return (
    <div className="page-content">
      <div className="projects-listing collaborators-directory">
        <PageHeader
          className="projects-listing__header"
          title="Collaborators"
          subtitle="Browse the people you work with and jump into their profiles."
          actions={
          <div className="projects-listing__controls">
            <SearchInput
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by name..."
              iconSrc={ICONS.search}
            />
            <FilterDropdown
              filters={[
                {
                  key: 'status',
                  type: 'select',
                  label: 'Status',
                  multiple: false,
                  options: STATUS_FILTER_OPTIONS,
                  value: statusFilter,
                  onChange: (next) => {
                    setStatusFilter(
                      Array.isArray(next) ? next[0] || '' : next || ''
                    );
                    setCurrentPage(1);
                  },
                },
                {
                  key: 'sortBy',
                  type: 'sortBy',
                  label: 'Sort By',
                  value: sortBy,
                  defaultValue: DEFAULT_SORT_BY,
                  onChange: (next) => {
                    setSortBy(next || DEFAULT_SORT_BY);
                    setCurrentPage(1);
                  },
                  options: SORT_OPTIONS,
                },
                {
                  key: 'sortDirection',
                  type: 'sortDirection',
                  label: 'Order',
                  value: sortDirection,
                  defaultValue: DEFAULT_SORT_DIRECTION,
                  onChange: (next) => {
                    setSortDirection(next || DEFAULT_SORT_DIRECTION);
                    setCurrentPage(1);
                  },
                },
              ]}
            />
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={() => setIsInviteOpen(true)}
            >
              Invite Collaborator
            </CustomButton>
          </div>
          }
        />

        <div className="projects-listing__table-wrapper">
          <div className="projects-listing__table" aria-busy={isLoading || undefined}>
            <div className="projects-listing__table-row projects-listing__table-row--head">
              <div>Collaborators Name</div>
              <div>Role</div>
              <div>Status</div>
              <div>XP</div>
              <div>Projects</div>
              <div>Joined on</div>
              <div className="projects-listing__table-actions-head">Actions</div>
            </div>

            {isLoading &&
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="projects-listing__table-row projects-listing__table-row--skeleton"
                  aria-hidden="true"
                >
                  {Array.from({ length: TABLE_COLUMNS }).map((__, cellIdx) => (
                    <div key={cellIdx} className="projects-listing__skeleton-cell">
                      <div className="projects-listing__skeleton-bar" />
                    </div>
                  ))}
                </div>
              ))}

            {!isLoading &&
              collaborators.map((person) => {
                const isYou = isSameUserId(person.userId, currentUserId);
                return (
                <div key={person.id} className="projects-listing__table-row">
                  <div className="projects-listing__table-cell projects-listing__table-cell--title">
                    <Avatar src={person.avatar} name={person.name} size="sm" />
                    <TruncatedText className="projects-listing__table-title">
                      {nameWithYou(person.name, isYou)}
                    </TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{person.role}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <span
                      className={`collaborators-directory__status collaborators-directory__status--${person.statusClass}`}
                    >
                      {person.statusLabel}
                    </span>
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{person.xp}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{person.projects}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{person.joinedOn}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell projects-listing__table-cell--actions">
                    <CustomButton
                      variant="secondary"
                      size="sm"
                      className="collaborators-directory__profile-btn"
                      trailingIconSrc={ICONS.arrowRight12}
                      trailingIconAlt=""
                      disabled={!person.userId}
                      onClick={() => openProfile(person.userId)}
                    >
                      View Profile
                    </CustomButton>
                  </div>
                </div>
              );
              })}

            {!isLoading && error && (
              <EmptyState
                title="Unable to load collaborators"
                description={error}
              />
            )}
            {!isLoading && !error && collaborators.length === 0 && (
              <EmptyState
                title="No Collaborators Yet"
                description="Invite collaborators to start working together on your projects."
                actionText="Invite Collaborator"
                onActionClick={() => setIsInviteOpen(true)}
              />
            )}
          </div>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          itemLabel="Collaborators"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />

        <AddCollaboratorModal
          isOpen={isInviteOpen}
          onClose={() => setIsInviteOpen(false)}
          showProjectSelect
          title="Invite Collaborator"
          subtitle="Select a project and invite collaborators to join."
          onInvited={fetchCollaborators}
        />
      </div>
    </div>
  );
};

export default Collaborators;
